import JourneyCardTimeline from '@/components/purchase/journey/JourneyCardTimeline';
import JourneyWarningMessage from '@/components/purchase/journey/JourneyWarningMessage';
import ServiceTextButton from '@/components/purchase/journey/ServiceTextButton';
import { showJourneyMap } from '@/features/map-component/mapComponentActions';
import useLocalizedCurrencyFormatter from '@/hooks/useLocalizedCurrencyFormatter';
import { useTenantConfiguration } from '@/hooks/useTenantConfiguration';
import { TransText } from '@/i18n/trans/text';
import { useDispatch } from '@/store/utils';
import type {
  BookedTripDTO,
  OffersWarningDTO,
  SearchJourneyOffersDTO,
} from '@/types/dto/agent';
import { breakpoints } from '@/utils/breakpoints';
import { cn } from '@/utils/cn';
import { formatDuration, formatTime } from '@/utils/dateTime';
import { trackCustomEvent, type GTM_EVENT } from '@/utils/gtm';
import {
  Badge,
  Button,
  Icons,
  Typography,
} from '@packages/webshop-design-system';
import type { ActionCreatorWithPayload } from '@reduxjs/toolkit';
import { useMemo, type FC } from 'react';
import { useMediaQuery } from 'react-responsive';

interface JourneyCardProps {
  journey: SearchJourneyOffersDTO;
  bookedTrip?: BookedTripDTO | null;
  warning: OffersWarningDTO | null;
  onContinue: () => void;
  setSelectedJourney: ActionCreatorWithPayload<SearchJourneyOffersDTO>;
  journeySelectedEvent: GTM_EVENT;
}
function renderPrice(
  amount: number | undefined,
  numberFormatter: Intl.NumberFormat
) {
  if (amount === undefined) {
    return null;
  }

  const parts = numberFormatter.formatToParts(amount);

  const integerParts = parts
    .filter((part) => part.type === 'integer' || part.type === 'group')
    .map((part) => part.value)
    .join('');
  const decimalPart =
    parts.find((part) => part.type === 'fraction')?.value || '00';
  const decimalSeparator =
    parts.find((part) => part.type === 'decimal')?.value || '.';
  const [currency, isCurrencyBeforePrice] = (() => {
    const currencyIndex = parts.findIndex((part) => part.type === 'currency');

    return [parts[currencyIndex]?.value || '', currencyIndex === 0];
  })();

  return (
    <>
      {isCurrencyBeforePrice && (
        <Typography variant="subtitle" className="mr-1">
          {currency}
        </Typography>
      )}
      <Typography variant="heading2">{integerParts}</Typography>
      {decimalPart ? (
        <>
          <Typography variant="heading2">{decimalSeparator}</Typography>
          <Typography variant="subtitle">
            {decimalPart.padEnd(2, '0')}
          </Typography>
        </>
      ) : null}
      {!isCurrencyBeforePrice && (
        <Typography variant="subtitle" className="ml-1">
          {currency}
        </Typography>
      )}
    </>
  );
}

const JourneyCard: FC<JourneyCardProps> = ({
  journey,
  bookedTrip,
  warning,
  onContinue,
  setSelectedJourney,
  journeySelectedEvent,
}) => {
  const dispatch = useDispatch();
  const tenantConfig = useTenantConfiguration();
  const numberFormatter = useLocalizedCurrencyFormatter();
  const enableMapComponent = !!tenantConfig?.enableMapComponent;
  const enableJourneyCardTimeline = !!tenantConfig?.enableJourneyCardTimeline;
  const isDisabled = !!warning || !journey.lowestPrice;
  const isCurrentTrip = Boolean(
    bookedTrip &&
      bookedTrip.departureTime === journey.departureTime &&
      bookedTrip.arrivalTime === journey.arrivalTime
  );
  const isLaptopOrBigger = useMediaQuery({
    minWidth: breakpoints.laptop,
  });

  const departureTime =
    journey.departureTime && formatTime(journey.departureTime);
  const arrivalTime = journey.arrivalTime && formatTime(journey.arrivalTime);
  const hasDiscount = Boolean(
    journey.trips?.find((trip) =>
      trip.offers?.find((offer) => offer.appliedReductions?.length)
    )
  );
  const serviceTexts = (journey.trips ?? []).flatMap((trip) =>
    (trip.legs ?? []).flatMap((leg) =>
      (leg.serviceAttributes ?? []).filter(
        ({ code }) => code === 'SERVICE_TEXT'
      )
    )
  );

  const isMapButtonVisible = useMemo(() => {
    return Boolean(
      enableMapComponent &&
        journey.trips?.some((trip) =>
          trip.legs?.some((leg) => Boolean(leg.encodedGeometry))
        )
    );
  }, [enableMapComponent, journey]);

  return (
    <>
      <div
        className={cn(
          'flex flex-col gap-2 rounded-l-xl border border-r-0 border-neutral bg-white px-4 py-3',
          {
            'bg-greyscale-100 text-neutral': isDisabled,
          }
        )}
      >
        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between gap-3 py-1 text-dark">
            <div className="flex min-w-0 flex-row items-center gap-1">
              <Typography variant="times" as="h2">
                {departureTime}
              </Typography>
              <Typography variant="times">
                <span>-</span>
              </Typography>
              <Typography variant="times" as="h2">
                {arrivalTime}
              </Typography>
            </div>
            <div className="flex self-start">
              <ServiceTextButton serviceTexts={serviceTexts} />
            </div>
          </div>

          {enableJourneyCardTimeline && (
            <JourneyCardTimeline journey={journey} />
          )}

          <div
            className={cn('flex flex-row flex-wrap items-center', {
              'gap-2': !isLaptopOrBigger,
              'gap-4': isLaptopOrBigger,
            })}
          >
            <div className="flex gap-2">
              <div className="flex gap-1">
                <Icons.time
                  height={14}
                  width={14}
                  className={cn('text-primary', {
                    'text-neutral': isDisabled,
                  })}
                />
                <Typography variant="body2" className="text-neutral">
                  {formatDuration(journey.duration)}
                </Typography>
              </div>
              <div className="flex">
                {journey.transfers ? (
                  <Typography className="text-error-text" variant="body2">
                    <TransText
                      i18nKey="journeyTransfersCount"
                      values={{ count: journey.transfers }}
                    />
                  </Typography>
                ) : (
                  <Typography variant="body2" className="text-primary">
                    <TransText i18nKey="direct" />
                  </Typography>
                )}
              </div>
            </div>
            {isMapButtonVisible && (
              <div className="flex basis-full sm:basis-auto">
                <Button
                  className="h-auto w-auto shrink-0 gap-1 whitespace-nowrap p-0 text-primary"
                  data-testid="journey-card-map-button"
                  variant="ghost"
                  size="small"
                  startAdornment={<Icons.map height={14} width={14} />}
                  onClick={() => dispatch(showJourneyMap(journey))}
                >
                  <TransText i18nKey="viewOnMap" />
                </Button>
              </div>
            )}
            {isCurrentTrip && (
              <div className="ml-auto flex">
                <Badge
                  variant="info"
                  className="gap-2 py-0"
                  startAdornment={<Icons.check className="h-4 w-4" />}
                  text={<TransText i18nKey="currentTrip" />}
                />
              </div>
            )}
          </div>
        </div>
      </div>
      <div
        className={cn('rounded-r-xl border border-neutral bg-primary', {
          'bg-neutral': isDisabled, // bg-primary and bg-neutral used here to fill weird gap between button and parent container
        })}
      >
        <Button
          className="relative flex h-full w-full flex-col rounded-l-none rounded-r-xl border-none"
          isDisabled={isDisabled}
          data-testid="journey-card-action-button"
          onClick={() => {
            dispatch(setSelectedJourney(journey));
            trackCustomEvent(journeySelectedEvent);
            onContinue();
          }}
        >
          {isDisabled ? (
            <Typography variant="body1" className="max-w-[200px] text-wrap">
              <JourneyWarningMessage warning={warning} />
            </Typography>
          ) : (
            <div className="flex flex-col items-center gap-1">
              <Typography variant="body2">
                <TransText i18nKey="from" />
              </Typography>
              <div className="flex items-baseline">
                {renderPrice(journey.lowestPrice?.amount, numberFormatter)}
                <span>
                  <Icons.chevronRight className="-mb-1" />
                </span>
              </div>
            </div>
          )}
          {hasDiscount && (
            <Badge
              className="absolute -right-2 -top-0.5 rotate-[10deg]"
              variant="success"
            >
              <Icons.dollarCircle width={12} height={12} />
              <TransText i18nKey="discount" />
            </Badge>
          )}
        </Button>
      </div>
    </>
  );
};

export default JourneyCard;
